import { useListState } from "@/hooks/use-list-state";
import { useState, useMemo } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  insertFurnishingAddressBookSchema,
  type FurnishingAddressBookEntry,
} from "@shared/schema";
import AppLayout from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, ChevronRight, BookOpen, Search, MapPin } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import AddressAutocomplete from "@/components/address-autocomplete";
import { usePageTitle } from "@/hooks/use-page-title";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

const bookEntryFormSchema = insertFurnishingAddressBookSchema.omit({ userId: true });
type BookEntryFormData = z.infer<typeof bookEntryFormSchema>;

export default function AppFurnishingAddressBook() {
  const queryClient = useQueryClient();
  usePageTitle("Address Book");
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<FurnishingAddressBookEntry | null>(null);
  const [searchQuery, setSearchQuery] = useListState("furnishing-address-book:search", "");

  const form = useForm<BookEntryFormData>({
    resolver: zodResolver(bookEntryFormSchema),
    defaultValues: {
      nickname: "",
      label: "",
      addressLine1: "",
      addressLine2: null,
      city: "",
      state: "",
      zip: "",
      country: "US",
    },
  });

  const entriesQuery = useQuery<FurnishingAddressBookEntry[]>({
    queryKey: ["/api/app/furnishing-address-book"],
  });

  const entries = entriesQuery.data ?? [];

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return entries;
    const q = searchQuery.toLowerCase();
    return entries.filter(
      (e) =>
        String(e.nickname ?? "").toLowerCase().includes(q) ||
        String(e.label ?? "").toLowerCase().includes(q) ||
        String(e.addressLine1 ?? "").toLowerCase().includes(q) ||
        String(e.city ?? "").toLowerCase().includes(q)
    );
  }, [entries, searchQuery]);

  const createMutation = useMutation({
    mutationFn: async (data: BookEntryFormData) => {
      const res = await apiRequest("POST", "/api/app/furnishing-address-book", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-address-book"] });
      setDialogOpen(false);
      form.reset();
      toast({ title: "Address saved to book" });
    },
    onError: (err) => toast({ title: "Failed to save", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<BookEntryFormData> }) => {
      const res = await apiRequest("PATCH", `/api/app/furnishing-address-book/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-address-book"] });
      setEditingEntry(null);
      setDialogOpen(false);
      form.reset();
      toast({ title: "Address updated" });
    },
    onError: (err) => toast({ title: "Failed to update", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/app/furnishing-address-book/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/furnishing-address-book"] });
      toast({ title: "Address removed from book" });
    },
    onError: (err) => toast({ title: "Failed to delete", description: parseErrorMessage(err, "Please try again."), variant: "destructive" }),
  });

  const openAdd = () => {
    setEditingEntry(null);
    form.reset({ nickname: "", label: "", addressLine1: "", addressLine2: null, city: "", state: "", zip: "", country: "US" });
    setDialogOpen(true);
  };

  const openEdit = (entry: FurnishingAddressBookEntry) => {
    setEditingEntry(entry);
    form.reset({
      nickname: entry.nickname,
      label: entry.label,
      addressLine1: entry.addressLine1,
      addressLine2: entry.addressLine2 ?? null,
      city: entry.city,
      state: entry.state,
      zip: entry.zip,
      country: entry.country,
    });
    setDialogOpen(true);
  };

  const onSubmit = (data: BookEntryFormData) => {
    if (editingEntry) {
      updateMutation.mutate({ id: editingEntry.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <AppLayout>
      <div className="p-6">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
          <Link href="/app/furnishing-catalog">
            <span className="hover:text-gray-700 cursor-pointer" data-testid="link-breadcrumb-catalog">Furnishing</span>
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-gray-900">Address Book</span>
        </div>

        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1
              className="text-2xl font-bold text-gray-900 flex items-center gap-2"
              data-testid="text-address-book-title"
              data-tour="furnishing-address-book-heading"
            >
              <BookOpen className="h-6 w-6 text-gray-500" />
              Address Book
            </h1>
            <p className="text-sm text-gray-500 mt-1">Saved addresses available across all your properties</p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="furnishing-address-book" />
            <GuideButton pageKey="furnishing-address-book" />
            <Button onClick={openAdd} data-testid="button-add-book-entry" data-tour="furnishing-address-book-add">
              <Plus className="h-4 w-4 mr-1.5" />
              Add Address
            </Button>
          </div>
        </div>

        <GuideNudgeBanner pageKey="furnishing-address-book" />

        <div className="mb-4" data-tour="furnishing-address-book-search">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by nickname, label, street, or city..."
              className="pl-9"
              data-testid="input-address-book-filter"
            />
          </div>
        </div>

        {entriesQuery.isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border rounded-lg">
            <MapPin className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">
              {searchQuery ? "No addresses match your search" : "Your address book is empty"}
            </p>
            {!searchQuery && (
              <p className="text-sm text-gray-400 mt-1">Save frequently-used addresses here to reuse them across any property.</p>
            )}
            {!searchQuery && (
              <Button className="mt-4" onClick={openAdd} data-testid="button-add-first-book-entry">
                <Plus className="h-4 w-4 mr-1.5" />
                Add First Address
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((entry) => (
              <div key={entry.id} className="border rounded-lg p-4" data-testid={`card-book-entry-${entry.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm" data-testid={`text-book-nickname-${entry.id}`}>{entry.nickname}</p>
                    <p className="text-xs text-gray-500 mt-0.5" data-testid={`text-book-label-${entry.id}`}>{entry.label}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(entry)} data-testid={`button-edit-book-entry-${entry.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => deleteMutation.mutate(entry.id)}
                      data-testid={`button-delete-book-entry-${entry.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  <p>{entry.addressLine1}</p>
                  {entry.addressLine2 && <p>{entry.addressLine2}</p>}
                  <p>{entry.city}, {entry.state} {entry.zip}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditingEntry(null); }}>
        <DialogContent
          className="max-w-md flex flex-col max-h-[90vh]"
          data-testid="dialog-book-entry"
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement | null;
            if (target?.closest?.('[data-address-autocomplete-portal]')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{editingEntry ? "Edit Address" : "Add to Address Book"}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
              <div className="overflow-y-auto flex-1 space-y-4 pr-1">
                <FormField control={form.control} name="nickname" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nickname *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder='e.g., "Tiago staging" or "Anna property"' data-testid="input-book-nickname" />
                    </FormControl>
                    <FormDescription className="text-xs text-gray-500">A short name for quick reference — only you see this.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="label" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Recipient name / label *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder='e.g., Example — Unit A Room 1' data-testid="input-book-label" />
                    </FormControl>
                    <FormDescription className="text-xs text-gray-500">
                      This becomes line 1 of the shipping label — e.g. <em>Example — Common Area</em>.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="addressLine1" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address Line 1 *</FormLabel>
                    <FormControl>
                      <AddressAutocomplete
                        value={field.value}
                        placeholder="123 Main St"
                        data-testid="input-book-address-line1"
                        onSelect={(_address, _coords, zipCode, city, state, streetAddress, country) => {
                          field.onChange(streetAddress || _address);
                          if (city) form.setValue("city", city, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                          if (state) form.setValue("state", state, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                          if (zipCode) form.setValue("zip", zipCode, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                          form.setValue("country", country || "US", { shouldDirty: true, shouldValidate: true, shouldTouch: true });
                        }}
                        onChange={(val) => field.onChange(val)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="addressLine2" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address Line 2</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ""} placeholder="Apt, Suite, etc." data-testid="input-book-address-line2" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <div className="grid grid-cols-3 gap-3">
                  <FormField control={form.control} name="city" render={({ field }) => (
                    <FormItem className="col-span-1">
                      <FormLabel>City *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Atlanta" data-testid="input-book-city" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="state" render={({ field }) => (
                    <FormItem>
                      <FormLabel>State *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="GA" data-testid="input-book-state" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="zip" render={({ field }) => (
                    <FormItem>
                      <FormLabel>ZIP *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="30301" data-testid="input-book-zip" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <FormField control={form.control} name="country" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="US" data-testid="input-book-country" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="flex justify-end gap-2 pt-4 flex-shrink-0 border-t mt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="button-cancel-book-entry">Cancel</Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending} data-testid="button-save-book-entry">
                  {editingEntry ? "Save" : "Add to Book"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
